import { useEffect, useState } from "react";
import api from "@/lib/api";
import KpiCard from "@/components/KpiCard";
import ActieImpactCard from "@/components/ActieImpactCard";
import PeriodFilter from "@/components/PeriodFilter";
import { Card } from "@/components/ui/card";
import { formatCurrency, formatNumber, formatPercent } from "@/lib/format";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Coins, ReceiptText, ShoppingBag, TrendingUp, PiggyBank, Calculator,
  Wallet, AlertOctagon, ChartLine, BookOpen,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  LineChart, Line, ComposedChart,
} from "recharts";

export default function Dashboard() {
  const today = new Date();
  const [jaren, setJaren] = useState([today.getFullYear()]);
  const [periode, setPeriode] = useState("jaar");
  const [jaar, setJaar] = useState(today.getFullYear());
  const [maand, setMaand] = useState(today.getMonth() + 1);
  const [kwartaal, setKwartaal] = useState(Math.floor(today.getMonth() / 3) + 1);
  const [overview, setOverview] = useState(null);
  const [evolution, setEvolution] = useState([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();
  const compactCurrency = (v) => {
    const n = Number(v) || 0;
    if (Math.abs(n) >= 1000) return `€${(n / 1000).toFixed(0)}k`;
    return `€${n.toFixed(0)}`;
  };

  useEffect(() => {
    api.get("/dashboard/jaren").then(({ data }) => {
      if (data?.length) {
        setJaren(data);
        if (!data.includes(jaar)) setJaar(data[0]);
      }
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { periode, jaar };
    if (periode === "maand") params.maand = maand;
    if (periode === "kwartaal") params.kwartaal = kwartaal;
    api.get("/dashboard/overview", { params })
      .then(({ data }) => setOverview(data))
      .finally(() => setLoading(false));
    api.get("/dashboard/evolution", { params: { jaar } }).then(({ data }) => setEvolution(data?.data || []));
  }, [periode, jaar, maand, kwartaal]);

  const periodeLabel = () => {
    if (periode === "maand") return `${new Date(2000, maand - 1).toLocaleString("nl-BE", { month: "long" })} ${jaar}`;
    if (periode === "kwartaal") return `Q${kwartaal} ${jaar}`;
    if (periode === "ytd") {
      const maxM = overview?.ytd_max_maand;
      const currentYear = new Date().getFullYear();
      if (maxM && jaar === currentYear) {
        const monthName = new Date(2000, maxM - 1).toLocaleString("nl-BE", { month: "long" });
        return `YTD ${jaar} (jan–${monthName})`;
      }
      return `YTD ${jaar}`;
    }
    return String(jaar);
  };

  // Gemiddelde netto marge over de getoonde maanden (null-waarden = demo-modus → overslaan)
  const nettoWaarden = evolution.filter((m) => m.netto_marge != null);
  const maandenMetData = nettoWaarden.length;
  const avgNettoMarge = maandenMetData
    ? nettoWaarden.reduce((s, m) => s + m.netto_marge, 0) / maandenMetData
    : null;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500">Overzicht</p>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-bold text-slate-900 mt-1">
            Dashboard <span className="text-slate-400">·</span> <span className="text-slate-500 font-medium">{periodeLabel()}</span>
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Omzet, marges en KPI&apos;s voor je Lifjalla webshop.
          </p>
        </div>
        <PeriodFilter
          periode={periode} jaar={jaar} maand={maand} kwartaal={kwartaal} jaren={jaren}
          onChange={({ periode: p, jaar: y, maand: m, kwartaal: k }) => {
            setPeriode(p); setJaar(y); setMaand(m); setKwartaal(k);
          }}
        />
      </header>

      {/* Actie-impact (alleen zichtbaar tijdens/net na een actieperiode) */}
      <ActieImpactCard />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Omzet (incl. btw)"
          value={loading ? "…" : formatCurrency(overview?.omzet_incl_btw)}
          sublabel={`${formatNumber(overview?.aantal_orders)} betaalde orders`}
          icon={Coins} accent="sky" testId="kpi-omzet-incl"
        />
        <KpiCard
          label="Geboekte omzet (excl. btw)"
          value={loading ? "…" : formatCurrency(overview?.geboekte_omzet_excl_btw)}
          sublabel={`${formatNumber(overview?.aantal_orders_geboekt)} orders incl. nog te verwerken`}
          icon={BookOpen} accent="sky" testId="kpi-geboekt-excl"
        />
        <KpiCard
          label="Omzet (excl. btw)"
          value={loading ? "…" : formatCurrency(overview?.omzet_excl_btw)}
          sublabel={`Betaalde orders · BTW: ${formatCurrency(overview?.btw_totaal)}`}
          icon={ReceiptText} testId="kpi-omzet-excl"
        />
        <KpiCard
          label="Gemiddelde orderwaarde"
          value={loading ? "…" : formatCurrency(overview?.gemiddelde_orderwaarde)}
          sublabel={`Verzendkosten: ${formatCurrency(overview?.verzendkosten_totaal)}`}
          icon={ShoppingBag} testId="kpi-aov"
        />
        <KpiCard
          label="Brutomarge"
          value={loading ? "…" : formatCurrency(overview?.brutomarge)}
          sublabel={loading ? "" : `${formatPercent(overview?.brutomarge_percentage)} op excl. btw`}
          icon={TrendingUp} accent="emerald" testId="kpi-bruto"
        />
        <KpiCard
          label="Operationele marge"
          value={loading ? "…" : formatCurrency(overview?.operationele_marge)}
          sublabel={`Na betaal- en boekhoudkost`}
          icon={Calculator} accent="emerald" testId="kpi-operationeel"
        />
        <KpiCard
          label="Netto marge"
          value={loading ? "…" : formatCurrency(overview?.netto_marge)}
          sublabel={`Vaste kosten: ${formatCurrency(overview?.vaste_kosten_totaal)}`}
          icon={PiggyBank} accent={overview?.netto_marge >= 0 ? "emerald" : "rose"} testId="kpi-netto"
        />
        <KpiCard
          label="Ø marge / order"
          value={loading ? "…" : formatCurrency(overview?.gemiddelde_marge_per_order)}
          sublabel={`Betaalkost: ${formatCurrency(overview?.betaalkost_totaal)}`}
          icon={Wallet} testId="kpi-marge-order"
        />
        <KpiCard
          label="Controle nodig"
          value={loading ? "…" : formatNumber(overview?.controle_nodig)}
          sublabel={`Uitgesloten: ${formatNumber(overview?.uitgesloten_orders)} orders`}
          icon={AlertOctagon} accent={overview?.controle_nodig > 0 ? "amber" : "slate"} testId="kpi-controle"
        />
      </div>

      {/* Evolutie charts */}
      <Card className="bg-white border-slate-200 p-4 sm:p-6 rounded-lg" data-testid="evolution-card">
        <div className="flex items-center justify-between mb-4 sm:mb-5">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500">Evolutie</p>
            <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mt-0.5 flex items-center gap-2">
              <ChartLine className="h-5 w-5 text-sky-600" />
              <span className="leading-tight">Omzet en marge per maand in {jaar}</span>
            </h2>
          </div>
        </div>
        <div className="h-[280px] sm:h-[340px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={evolution}
              barCategoryGap={isMobile ? 6 : 20}
              margin={{ top: 8, right: isMobile ? 6 : 16, left: isMobile ? -16 : 0, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="label"
                tick={{ fill: "#64748b", fontSize: isMobile ? 9 : 12 }}
                stroke="#cbd5e1"
                tickFormatter={(v) => (isMobile && v?.length > 4 ? v.slice(-2) : v)}
                interval={0}
              />
              <YAxis
                yAxisId="left"
                tick={{ fill: "#64748b", fontSize: isMobile ? 9 : 12 }}
                stroke="#cbd5e1"
                tickFormatter={isMobile ? compactCurrency : undefined}
                width={isMobile ? 44 : 60}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fill: "#10b981", fontSize: isMobile ? 9 : 12 }}
                stroke="#10b981"
                tickFormatter={(v) => `${v}%`}
                domain={[0, "auto"]}
                width={isMobile ? 32 : 40}
              />
              <Tooltip
                contentStyle={{ background: "white", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: 12 }}
                formatter={(value, name) =>
                  name === "Brutomarge %" ? `${Number(value).toFixed(1)}%` : formatCurrency(value)
                }
              />
              <Legend wrapperStyle={{ fontSize: isMobile ? 10 : 12, paddingTop: 4 }} />
              <Bar yAxisId="left" dataKey="omzet_excl_btw" name="Omzet excl. btw" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="left" dataKey="operationele_marge" name="Operationele marge" fill="#64748b" radius={[4, 4, 0, 0]} />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="brutomarge_percentage"
                name="Brutomarge %"
                stroke="#10b981"
                strokeWidth={2.5}
                dot={{ fill: "#10b981", r: isMobile ? 3 : 4 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="bg-white border-slate-200 p-4 sm:p-6 rounded-lg" data-testid="netto-evolution-card">
        <div className="mb-4 sm:mb-5">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500">Netto marge</p>
          <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mt-0.5 leading-tight" data-testid="netto-avg-title">
            Netto marge per maand — gemiddeld {formatCurrency(avgNettoMarge)}/mnd
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Na aftrek van {formatCurrency(evolution?.[0]?.vaste_kosten)} vaste kosten per maand · gemiddelde over {maandenMetData} getoonde maand{maandenMetData === 1 ? "" : "en"}
          </p>
        </div>
        <div className="h-[240px] sm:h-[280px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={evolution} margin={{ top: 8, right: isMobile ? 6 : 16, left: isMobile ? -16 : 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="label"
                tick={{ fill: "#64748b", fontSize: isMobile ? 9 : 12 }}
                stroke="#cbd5e1"
                tickFormatter={(v) => (isMobile && v?.length > 4 ? v.slice(-2) : v)}
                interval={0}
              />
              <YAxis
                tick={{ fill: "#64748b", fontSize: isMobile ? 9 : 12 }}
                stroke="#cbd5e1"
                tickFormatter={isMobile ? compactCurrency : undefined}
                width={isMobile ? 44 : 60}
              />
              <Tooltip
                contentStyle={{ background: "white", border: "1px solid #e2e8f0", borderRadius: "8px", fontSize: 12 }}
                formatter={(value, name) =>
                  name === "Brutomarge %" ? `${Number(value).toFixed(1)}%` : formatCurrency(value)
                }
              />
              <Legend wrapperStyle={{ fontSize: isMobile ? 10 : 12, paddingTop: 4 }} />
              <Line type="monotone" dataKey="netto_marge" name="Netto marge" stroke="#0ea5e9" strokeWidth={2.5} dot={{ fill: "#0ea5e9", r: isMobile ? 3 : 4 }} />
              <Line type="monotone" dataKey="brutomarge_percentage" name="Brutomarge %" stroke="#10b981" strokeWidth={2} dot={false} yAxisId="right" />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: "#10b981", fontSize: isMobile ? 9 : 12 }} stroke="#10b981" width={isMobile ? 32 : 40} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}
